/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { addScormResponseAction } from 'actions/addScormResponseAction';
import { updateScoreAction } from 'actions/updateScoreAction';
import EditableBtn from 'components/lib/EditableBtn';
import Feedback from 'components/lib/Feedback';
import { SCORM } from 'pipwerks-scorm-api-wrapper';
import { Fragment as _, useContext, useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import getBlocksByType from 'services/apiHelpers/getBlocksByType';
import renderBlock from 'services/apiHelpers/renderBlock';
import { useScoringQuizzes } from 'services/apiHelpers/useScoringQuizzes';
import { findByKeyVal_V2 } from 'services/findByKeyVal';
import { getScore, setScore } from 'services/scoring';
import { unlockInteraction } from 'services/unlockInteraction';
import { Report, State } from 'store/store';
import { colors, getButtonFocus } from 'styles/variables';
import { interactionContext } from './interactionContext';
import Option from './Option';
import hexToRgb from 'services/hexToRGB';
import pubSub from 'services/pubSub';

import { HostingContext } from 'hosting/context';
import { sortationUserResponseEvent } from 'hosting/events';

const { lGrey0, lGrey2 } = colors;

let sessionLock = {};
let attempts = {};

pubSub.subscribe('RESET_MODULE', () => {
  console.log('sortation resets');

  sessionLock = {};
  attempts = {};
});

export const sortationDefaultStyling = {
  baseColor: lGrey2,
  baseColorOpacity: 100,
  baseOutline: false,
  baseOutlineColor: '',
  baseBorderRadius: 10,
  // numberOfAttempts,
  score: true,
  showCorrectAnswer: 0,
};

function shuffle(array) {
  let m = array.length,
    t,
    i;

  let n = [...array];

  // While there remain elements to shuffle…

  while (m) {
    // Pick a remaining element…

    const i = Math.floor(Math.random() * m--);

    // And swap it with the current element.

    t = n[m];

    n[m] = n[i];

    n[i] = t;
  }

  i = Math.floor(Math.random() * n.length);

  if (n.every((option, k) => option.id === array[k].id)) {
    let j = Math.floor(Math.random() * n.length);

    while (j === i) {
      j = Math.floor(Math.random() * n.length);
    }

    t = n[i];

    n[i] = n[j];

    n[j] = t;
  }

  return n;
}

const Sortation = props => {
  const context = useContext(interactionContext);
  const hostingCtx = useContext(HostingContext);

  const {
    baseColor: _baseColor = lGrey2,
    baseColorOpacity: _baseColorOpacity = 100,
    baseOutline = false,
    baseOutlineColor = '',
    baseBorderRadius = 10,
    // numberOfAttempts,
    score,
    showCorrectAnswer,
    showAttemptNumber,
    useMediaInFeedback = true,
    useFeedback = true,
    navigationBehaviour = {
      correct: false,
      incorrect: false,
    },
    // direction = 'vertical',
  } = context.props;

  let direction = context.props.direction || 'vertical';

  const { r, g, b } = hexToRgb(_baseColor);
  const baseColorOpacity = _baseColorOpacity * 0.01;
  const baseColor = `rgba(${r}, ${g}, ${b}, ${baseColorOpacity})`;

  const numberOfAttempts = context.hasAttemptFeedback
    ? context.props.numberOfAttempts
    : 0;

  const { id, children } = props;

  let questionText =
    children[0]?.props?.type === 'SortationQuestionText' ? children[0] : null;

  const answers = getBlocksByType('Answer', 'id');
  const heading0 = getBlocksByType('SortationHeading0', 'id');
  const heading1 = getBlocksByType('SortationHeading1', 'id');
  const options = children
    .filter(c => answers.includes(c.props.id))
    .map((c, i) => {
      const { id, children } = c.props;
      return { id, children };
    });
  const sortFrom = children.find(c => heading0.includes(c.props.id));
  const sortTo = children.find(c => heading1.includes(c.props.id));

  const [order, setOrder] = useState(() =>
    !window.dev() ? shuffle(options) : options
  );

  const [isLive, setIsLive] = useState(false);
  const [submit, setSubmit] = useState(sessionLock[context.id] ? 2 : 0);

  const scoringQuizzes = useScoringQuizzes();
  const { scoreAll } = useSelector((state: State) => {
    return Object.values(state.blocks).find(b => b.type === 'Module')
      ?.props as any;
  });

  const sortation = useRef<HTMLDivElement>(null);
  const hasAttempt = useRef<Boolean>(false);

  if (!attempts[context.id]) {
    attempts[context.id] = 0;
  }

  const isCorrect = useRef(false);

  sessionLock[context.id] =
    attempts[context.id] === numberOfAttempts &&
    score &&
    getScore(context.id) !== undefined;

  const [feedback, setFeedback] = useState(false);

  // console.log({ direction_ });
  // const [direction, setDirection] = useState<'vertical' | 'horizontal'>(
  //   direction_
  // );

  // console.log({ direction });

  const [localDirection, setLocalDirection] = useState<
    'vertical' | undefined
  >();

  if (localDirection) {
    direction = localDirection;
  }

  // console.log({ localDirection });

  const dispatch = useDispatch();

  const updateOrder = $ => {
    const { source, destination } = $;

    const reorder = (list, startIndex, endIndex) => {
      const reordered = [...list];
      const [removed] = reordered.splice(startIndex, 1);
      reordered.splice(endIndex, 0, removed);

      return reordered;
    };

    const newOrder = reorder(order, source.index, destination.index);

    isCorrect.current = options.every((o, i) => o.id === newOrder[i].id);

    setScore(context.id, isCorrect.current);
    setOrder(newOrder);
  };

  const showFeedback = correct => {
    useFeedback && setFeedback(true);

    if (window.dev()) {
      return;
    }

    const alphabetToArray = 'abcdefghijklmnopqrstuvwxyz'.split('');

    const optionsIds = options.map(o => o.id || o.props.id);
    const orderIds = order.map(o => o.id || o.props.id);

    const getCorrectAndResponseOptions = entry => {
      let optionText: HTMLElement | string = document.querySelector(
        `#sortation-option-${entry.id}`
      ) as HTMLElement;

      if (optionText) {
        optionText = optionText.innerText;
      }

      return optionText || '';
    };

    const sortationResolved =
      correct || attempts[context.id] === numberOfAttempts;

    if (sortationResolved && showCorrectAnswer === 1) {
      setOrder(options);
    }

    if (sortationResolved) {
      // && score
      const alphabetized = Array.from(
        sortation.current!.querySelectorAll('.sortation-option')
      ).map((option: any) => {
        const id = option.id.replace('sortation-option-', '');
        return 'abcdefghijklmnopqrstuvwxyz'.split('')[
          options.findIndex((o: any) => o.id === id)
        ];
      });

      // dispatch if there's no scoringQuizzes etc
      let shouldDispatchScore = score;
      //
      if (scoringQuizzes.length) {
        shouldDispatchScore = false;
      }

      let questionText:
        | NodeList
        | string[]
        | string = document.querySelectorAll(
        `#i${context.id} [class*="SortationHeading"]`
      ) as NodeList;

      if (questionText) {
        questionText = Array.from(questionText).map(q => {
          return (q as HTMLElement).innerText;
        });

        questionText = `Sort items from ${questionText[0]} to ${questionText[1]}`;
      }

      const correctExpanded = alphabetToArray
        .slice(0, options.length)
        .join(SCORM.version == '1.2' ? ',' : '[,]');

      const responseExpanded = orderIds
        .map(id => {
          return alphabetToArray[optionsIds.indexOf(id)];
        })
        .join(SCORM.version == '1.2' ? ',' : '[,]');

      //
      const isCorrectCurrentToScore: 0 | 1 = isCorrect.current ? 1 : 0;

      const action = {
        id: context.id,
        type: 'sequencing',
        question: questionText,
        reportable: 'sortation',
        response: responseExpanded,
        correct: correctExpanded,
        // @sam. updated this from the bool it was. to send a 1 | 0
        result: isCorrectCurrentToScore,
      } as Report;

      //

      hostingCtx?.data(
        sortationUserResponseEvent(context.id, {
          order: orderIds,
          //order.map(o => getCorrectAndResponseOptions(o)).join(),
          success: isCorrect.current,
          attemptNumber: attempts[context.id],
        })
      );

      if (shouldDispatchScore) {
        dispatch(updateScoreAction(context.id, isCorrectCurrentToScore));
      }

      dispatch(addScormResponseAction(action));
    }

    if (isCorrect.current || attempts[context.id] === numberOfAttempts) {
      setSubmit(2);
      unlockInteraction(context.id);
    }
  };

  const onClose = () => {
    setFeedback(false);
  };

  //
  const idDep = options
    .map(entry => {
      return entry.id;
    })
    .join('_');

  //
  useEffect(() => {
    if (!window.dev()) {
      return;
    }

    setOrder(options);
  }, [idDep]);
  //

  useEffect(() => {
    if (hasAttempt.current && !isLive) {
      setIsLive(true);
    }

    if (window.dev()) {
      if (parent.showFeedback) {
        parent.showFeedback[context.id] = correct => {
          if (feedback) {
            setFeedback(false);
            requestAnimationFrame(() =>
              showFeedback((isCorrect.current = correct))
            );
            //
          } else {
            showFeedback((isCorrect.current = correct));
          }
        };
      }
    }

    if (order.length !== options.length) {
      setOrder(options);
    }
  });

  const SubmitBtn: any = renderBlock(
    findByKeyVal_V2(props.id, 'type', 'EditableButton')!,
    {
      EditableButton: ({ ...props }) => (
        <EditableBtn
          {...{ enabled: !!isLive, ...props }}
          onClick={() => {
            attempts[context.id] += 1;
            showFeedback(isCorrect.current);
          }}
        />
      ),
    }
  );

  useEffect(() => {
    if (direction === 'vertical') {
      return;
    }

    var mq = window.matchMedia('(max-width: 768px)');

    onMQChange(mq);
    mq.addEventListener('change', onMQChange);

    function onMQChange(mq) {
      setLocalDirection(mq.matches ? 'vertical' : undefined);
    }

    return () => {
      mq.removeEventListener('change', onMQChange);
    };
  }, []);

  useEffect(() => {
    if (window.dev() || !sortation.current) {
      return;
    }

    const alphabetized = Array.from(
      sortation.current.querySelectorAll('.sortation-option')
    ).map((option: any) => {
      const id = option.id.replace('sortation-option-', '');
      return 'abcdefghijklmnopqrstuvwxyz'.split('')[
        options.findIndex((o: any) => o.id === id)
      ];
    });
  });

  let submitBtnTotalHeight;

  const getSubmitButtonTotalHeight = () => {
    const submitBtn = document.querySelector(
      `#sortation-submit-btn-${id} [class*='genericButton']`
    ) as HTMLElement;

    if (!submitBtn) {
      return;
    }
    //
    return submitBtn.getBoundingClientRect().height + 20;
  };

  let parsedIsCorrect;

  if (attempts[context.id] < numberOfAttempts) {
    parsedIsCorrect = isCorrect.current || 'attempt';
  } else {
    parsedIsCorrect = isCorrect.current;
  }

  return (
    <div
      css={css({
        wordBreak: 'break-word',
      })}
    >
      {questionText && (
        <div
          style={{
            margin: '0 0 20px 0',
          }}
        >
          {questionText}
        </div>
      )}
      <div
        ref={sortation}
        css={css`
          position: relative;
 
          width: 100%;
          // margin: ${getSubmitButtonTotalHeight()}px 0 0 0;
          padding: 10px 10px;

          border: 1px solid
            ${
              baseOutline && baseOutlineColor ? baseOutlineColor : 'transparent'
            };

          border-radius: ${baseBorderRadius}px;
          background: ${baseColor};
          pointer-events: ${submit === 0 ? 'auto' : 'none'};
        `}
      >
        {sortFrom}
        <DragDropContext
          onDragEnd={data => {
            if (!data.destination) {
              return;
            }

            updateOrder(data);
          }}
        >
          <Droppable
            droppableId={`Sortation_${id}`}
            type="droppableSortation"
            {...(direction === 'horizontal' && {
              direction,
            })}
          >
            {provided => (
              <div
                ref={provided.innerRef}
                css={css({
                  ...(direction === 'horizontal'
                    ? {
                        display: 'flex',
                      }
                    : {}),
                })}
              >
                {order.map((option, i) => (
                  <Draggable
                    key={option.id}
                    draggableId={option.id}
                    {...(window.dev() && {
                      isDragDisabled: true,
                    })}
                    index={i}
                  >
                    {(provided, snapshot) => {
                      if (!hasAttempt.current && snapshot.isDragging)
                        hasAttempt.current = true;
                      return (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          css={css`
                            // display: flex;
                            margin: 5px;
                            border-radius: 5px;

                            // flex: 1;
                            // outline: 2px solid red;
                            ${!snapshot.isDragging &&
                              `&:focus {
                              //
                              ${getButtonFocus(
                                context.props.panelBackground || lGrey0
                              )}
                            }`}
                          `}
                          style={{
                            ...provided.draggableProps.style,
                          }}
                        >
                          <Option
                            key={option.id}
                            option={option}
                            isDragging={snapshot.isDragging}
                          />
                        </div>
                      );
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {sortTo}
      </div>

      <div
        id={`sortation-submit-btn-${id}`}
        // class={}
        css={css`
          margin-top: 20px;
          ${!window.dev() &&
            `
            opacity: ${isLive && submit === 0 ? 1 : 0};
            pointer-events: ${isLive && submit === 0 ? 'auto' : 'none'};
            transition: opacity 0.2s;
          `}
        `}
      >
        {SubmitBtn}
      </div>

      {feedback && (
        <Feedback
          id={context.id}
          correct={window.dev() ? isCorrect.current : parsedIsCorrect}
          attempt={attempts[context.id]}
          numberOfAttempts={numberOfAttempts}
          showAttemptNumber={showAttemptNumber}
          showCorrectAnswer={showCorrectAnswer}
          useMediaInFeedback={useMediaInFeedback}
          navigationBehaviour={navigationBehaviour}
          onClose={() => {
            const tick = setInterval(() => {
              const submitButtonElement: any = document.querySelector(
                `#i${SubmitBtn?.props?.id}`
              );

              if (submitButtonElement) {
                submitButtonElement.focus();

                clearInterval(tick);
              }
            }, 0);

            onClose();
          }}
        />
      )}
    </div>
  );
};

export default Sortation;
